import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Roles } from '../../models/general';
import type { RootState } from '../../store/redux/store/store';
import Alert from '../ui/Alert';

const FX_SETTINGS_PATH = '/settings/currency-and-banks';
/** The route's SETTINGS_ROLES in App.tsx: anyone else would follow the link to a "no access" notice. */
const FX_SETTINGS_ROLES: string[] = [Roles.Hr, Roles.Admin];

type Props = {
  /** Sentence after "Exchange rate not configured": says what THIS screen did with those amounts. */
  hintKey: 'payroll.fx.unconvertedHint' | 'payroll.fx.shownInOriginalCurrency';
  /** Currency codes with no rate, when the screen knows them. */
  codes?: string[];
};

/**
 * The server answers a conversion with no configured rate by passing the amount through at 1:1 and
 * flagging the row `currencyRateUnavailable`. Without this notice the figure reads as a conversion.
 * Same wording and shape as the payroll dashboard's banner (CO-610).
 */
const FxMissingRateNotice: React.FC<Props> = ({ hintKey, codes }) => {
  const { t } = useTranslation('common');
  const role = useSelector((s: RootState) => s.user.role);
  const suffix = codes && codes.length > 0 ? ` (${codes.join(', ')})` : '';
  return (
    <Alert variant="warning" className="w-full items-center" data-testid="fx-missing-rate-notice">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <p className="flex-1 min-w-[20ch] text-sm">
          {`${t('payroll.fx.missingRate')}${suffix}. ${t(hintKey)}`}
        </p>
        {FX_SETTINGS_ROLES.includes(role) ? (
          <Link            to={FX_SETTINGS_PATH}            className="text-sm font-medium underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"            data-testid="fx-missing-rate-link"          >            {t('payroll.fx.configureRates')}          </Link>
        ) : (
          <p className="text-sm font-medium" data-testid="fx-missing-rate-ask-hr">{t('payroll.fx.askHr')}</p>
        )}
      </div>
    </Alert>
  );
};

export default FxMissingRateNotice;
